import React, { FC } from "react";
import classnames from "classnames";

import "./index.scss";

/**
 * The right slot of a SPARK card head. Three jobs, and the design's own component description
 * (Figma `SPARK/CardHead`, 9380:1302) is explicit that they are NOT interchangeable:
 *
 *   link    — arrow ON, label ON  → the head navigates ("See all")
 *   caption — arrow OFF, label ON → metadata ("21 of 84 earned", "never resets")
 *   none    — title and rule only
 *
 * and, verbatim: *"An arrow means the head navigates. Never leave it on for a caption."*
 *
 * Modelled as a discriminated union rather than independent `label` / `showArrow` / `onClick` props
 * so that rule is enforced by the compiler instead of by remembering it. An arrow pointing nowhere,
 * or a caption that navigates, cannot be written down.
 */
export type CardHeadSlot = { kind: "link"; label: string; onClick: () => void } | { kind: "caption"; label: string };

interface CardHeadProps {
  title: string;
  slot?: CardHeadSlot;
  className?: string;
}

/**
 * The arrow, inlined rather than imported from `assets/`.
 *
 * Two reasons, and the second is the one that matters. Jest resolves no path aliases in this repo, so
 * an `@assets/…svg` import would force every test that renders a card head — present and future — to
 * virtual-mock it. And every icon this app loads through `Icon` is a CSS background-image with its
 * colour BAKED into the file, which is why `wallet` can only ever sit on its pink tile. Inline SVG with
 * `currentColor` has neither problem: this arrow simply is whatever colour its label is.
 *
 * Drawn pointing right. The Figma asset is an up arrow that the design rotates per instance; rotating
 * the geometry once here is the same picture without a transform to keep in step with the text.
 */
const SlotArrow: FC = () => (
  <svg
    className="spark-card-head-arrow"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M3.33333 8H12.6667"
      stroke="currentColor"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 3.33333L12.6667 8L8 12.6667"
      stroke="currentColor"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * The section head every SPARK block shares (Figma `SPARK/CardHead`, 9380:1302): a title, a flexible
 * rule, and an optional right slot pinned to the trailing edge.
 *
 * The design's "rule" carries no fill — it is a 1px flex spacer whose only job is to push the slot to
 * the far edge — so it is reproduced as `space-between` rather than as a rendered element. Adding a
 * visible line here would draw something the design does not have.
 *
 * Presentational only: the slot's `onClick` is supplied by the caller, which owns the navigation.
 */
const CardHead: FC<CardHeadProps> = (props) => {
  const { title, slot, className } = props;

  return (
    <div className={classnames("spark-card-head", className)}>
      <div className="spark-card-head-title">{title}</div>
      {slot?.kind === "link" ? (
        <button type="button" className="spark-card-head-link" onClick={slot.onClick}>
          {slot.label}
          <SlotArrow />
        </button>
      ) : null}
      {slot?.kind === "caption" ? <span className="spark-card-head-caption">{slot.label}</span> : null}
    </div>
  );
};

export default CardHead;
